import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import { useTranslation } from "react-i18next";
import api from "../../services/api";

function normalizePayload(payload) {
  const body = payload?.data || payload || {};
  return body.data || body;
}

function arrayValue(value) {
  return Array.isArray(value) ? value : [];
}

function numberValue(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function recipeName(value, t) {
  return value || t("smart_kitchen.recommendations.unknown_recipe");
}

function benefitChips(benefit, t) {
  const fields = [
    ["inventory_used", "inventory_used"],
    ["shopping_reduction", "shopping_reduction"],
    ["waste_reduction", "waste_reduction"],
  ];
  return fields.map(([field, key]) => (
    <Chip
      key={field}
      size="small"
      variant="outlined"
      label={t(`smart_kitchen.recommendations.benefit.${key}`, {
        count: numberValue(benefit?.[field]),
      })}
    />
  ));
}

function recommendationMessage(recommendation, t) {
  const params = {
    item: recommendation?.inventory_item_name || "",
    current: recipeName(recommendation?.current_recipe_name, t),
    suggested: recipeName(recommendation?.suggested_recipe_name, t),
    days: recommendation?.days_until_expiry,
  };
  return t(`smart_kitchen.recommendations.message.${recommendation?.type}`, params);
}

function errorLabel(error, t) {
  if (error === "meal_plan_not_found") {
    return t("smart_kitchen.recommendations.error_meal_plan_not_found");
  }
  return error || t("smart_kitchen.recommendations.load_error");
}

export default function PantryRecommendationsDialog({ open, mealPlanId, onClose }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const loadRecommendations = useCallback(async () => {
    if (!mealPlanId) return;
    setLoading(true);
    setError("");
    try {
      const response = await api.smartKitchen.getMealPlanRecommendations(mealPlanId);
      setData(normalizePayload(response));
    } catch (err) {
      setData(null);
      setError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        t("smart_kitchen.recommendations.load_error")
      );
    } finally {
      setLoading(false);
    }
  }, [mealPlanId, t]);

  useEffect(() => {
    if (!open) return;
    setData(null);
    setError("");
    loadRecommendations();
  }, [loadRecommendations, open]);

  const recommendations = arrayValue(data?.recommendations);
  const summary = useMemo(() => data?.summary || {}, [data]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" data-testid="pantry-recommendations-dialog">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <TipsAndUpdatesRoundedIcon color="primary" />
        {t("smart_kitchen.recommendations.title")}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Alert severity="info">
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {t("smart_kitchen.recommendations.read_only")}
            </Typography>
            <Typography variant="body2">
              {t("smart_kitchen.recommendations.subtitle")}
            </Typography>
          </Alert>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : null}

          {error ? <Alert severity="error">{errorLabel(error, t)}</Alert> : null}

          {!loading && data && !recommendations.length && !error ? (
            <Alert severity="success" data-testid="pantry-recommendations-empty">
              {t("smart_kitchen.recommendations.empty")}
            </Alert>
          ) : null}

          {!loading && data && recommendations.length ? (
            <>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  color="primary"
                  label={t("smart_kitchen.recommendations.summary_count", {
                    count: numberValue(summary.recommendation_count || recommendations.length),
                  })}
                />
                <Chip
                  variant="outlined"
                  label={t("smart_kitchen.recommendations.summary_priority", {
                    high: numberValue(summary.high_priority_count),
                    medium: numberValue(summary.medium_priority_count),
                    low: numberValue(summary.low_priority_count),
                  })}
                />
              </Stack>

              <Stack spacing={1.5}>
                {recommendations.map((recommendation, index) => (
                  <Paper
                    key={`${recommendation?.type || "recommendation"}-${recommendation?.current_recipe_id || "none"}-${recommendation?.suggested_recipe_id || "none"}-${index}`}
                    variant="outlined"
                    sx={{ p: 1.5 }}
                    data-testid="pantry-recommendation-card"
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                            {t(`smart_kitchen.recommendations.type.${recommendation?.type}`)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {recommendationMessage(recommendation, t)}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          color={recommendation?.priority === "high" ? "warning" : "default"}
                          label={t(`smart_kitchen.recommendations.priority.${recommendation?.priority || "low"}`)}
                        />
                      </Stack>

                      {recommendation?.current_recipe_name || recommendation?.suggested_recipe_name ? (
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                          {recommendation?.current_recipe_name ? (
                            <Typography variant="body2">
                              <strong>{t("smart_kitchen.recommendations.current_recipe")}:</strong>{" "}
                              {recipeName(recommendation.current_recipe_name, t)}
                            </Typography>
                          ) : null}
                          {recommendation?.suggested_recipe_name ? (
                            <Typography variant="body2">
                              <strong>{t("smart_kitchen.recommendations.suggested_recipe")}:</strong>{" "}
                              {recipeName(recommendation.suggested_recipe_name, t)}
                            </Typography>
                          ) : null}
                        </Stack>
                      ) : null}

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                          {t("smart_kitchen.recommendations.expected_benefit")}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {benefitChips(recommendation?.expected_benefit || {}, t)}
                        </Stack>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("smart_kitchen.recommendations.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
